
import React, { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

interface VimeoPlayerProps {
  vimeoId: string;
  title?: string;
  minHeight?: string;
  autoplay?: boolean;
  loop?: boolean;
  responsive?: boolean;
  background?: boolean;
  muted?: boolean;
  controls?: boolean;
  preload?: 'auto' | 'metadata' | 'none';
  className?: string;
  lazyLoad?: boolean;
  startTime?: number;
  onIframeLoad?: () => void;
  previewDuration?: number; // Duration in seconds for preview optimization
}

const VimeoPlayer: React.FC<VimeoPlayerProps> = ({
  vimeoId,
  title = 'Vimeo video',
  minHeight = '300px',
  autoplay = false,
  loop = true,
  responsive = true,
  background = false,
  muted = false,
  controls = true,
  preload = 'auto',
  className = '',
  lazyLoad = true,
  startTime = 0,
  onIframeLoad,
  previewDuration = 30,
}) => {
  const isMobile = useIsMobile();
  const [shouldLoad, setShouldLoad] = useState(!lazyLoad);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (!lazyLoad) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    const containerElement = document.getElementById(`vimeo-container-${vimeoId}`);
    if (containerElement) {
      observer.observe(containerElement);
    }

    return () => {
      observer.disconnect();
    };
  }, [vimeoId, lazyLoad]);
  
  const extractVimeoId = (idOrUrl: string): string => {
    if (!idOrUrl) return '';
    
    if (/^\d+$/.test(idOrUrl)) {
      return idOrUrl;
    }
    
    const match = idOrUrl.match(/(?:vimeo\.com\/|vimeo\.com\/video\/|player\.vimeo\.com\/video\/)(\d+)/);
    return match ? match[1] : '';
  };

  const cleanVimeoId = extractVimeoId(vimeoId);
  
  if (!cleanVimeoId) {
    console.error('Invalid Vimeo ID:', vimeoId);
    return (
      <div 
        style={{ minHeight }} 
        className={`w-full bg-gray-900 flex items-center justify-center text-white ${className}`}
      >
        No valid Vimeo ID provided
      </div>
    );
  }

  const vimeoParams = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    loop: loop ? '1' : '0',
    title: '0',
    byline: '0',
    portrait: '0',
    dnt: '1',
    responsive: responsive ? '1' : '0',
    background: background ? '1' : '0',
    muted: (muted || background) ? '1' : '0',
    controls: (!background && controls) ? '1' : '0',
    preload: preload,
    keyboard: background ? '0' : '1',
    quality: isMobile ? '360p' : '720p',
    transparent: '1',
    playsinline: '1',
    autopause: '0',
    // Optimize for preview by setting duration limits for faster loading
    ...(previewDuration && previewDuration < 60 && { speed: '1' }),
    ...(startTime > 0 && { '#t': startTime.toString() })
  });
  
  const containerStyle = isMobile
    ? { height: 'auto', width: '100%', paddingBottom: responsive ? '56.25%' : '0' }
    : { minHeight, paddingBottom: responsive && !background ? '56.25%' : '0' };
  
  return (
    <div 
      id={`vimeo-container-${vimeoId}`}
      className={`w-full relative overflow-hidden ${className}`} 
      style={containerStyle}
    >
      {shouldLoad ? (
        <iframe 
          src={`https://player.vimeo.com/video/${cleanVimeoId}?${vimeoParams.toString()}`}
          width="100%" 
          height="100%" 
          frameBorder="0" 
          allow="autoplay; fullscreen; picture-in-picture" 
          loading={lazyLoad ? "lazy" : "eager"}
          title={title}
          className="absolute inset-0 w-full h-full"
          style={{ border: 'none', outline: 'none' }}
          onLoad={onIframeLoad} // NEW: signal iframe load
        />
      ) : (
        <div className="absolute inset-0 w-full h-full bg-gray-900 flex items-center justify-center">
          <div className="loading-spinner w-8 h-8 border-t-2 border-gray-200 rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

export default VimeoPlayer;
